import { memo } from 'react';
import { FV } from './share';
import { paymentRelatedScenarios } from '../config';
import FormField from 'components/FormField';
import { InputNumber, Radio, Select } from 'antd';
import { DisabledProps, RegionProps, ScenarioTypeProps } from '../TaskForm/share';
import { regionMonetaryUnitMap } from 'services/config/region';
import toLowerCase from 'utils/toLowerCase';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { isAeProduct } from 'productLine';
import { useFormikContext } from 'formik';

function OrderAmountField({ region, disabled }: RegionProps & DisabledProps) {
  const { t } = useTranslate();

  const label = t('aide.限定订单金额');

  const {
    values: { limitRule: { limitOrderAmount } = {} },
  } = useFormikContext<FV>();

  return (
    <FormField<FV['limitRule']['limitOrderAmount']>
      required
      name="limitRule.limitOrderAmount"
      label={label}
      style={
        limitOrderAmount?.enable ? { marginBottom: 0, transition: 'none' } : { transition: 'none' }
      }
    >
      {({ field: { value, onChange, onBlur } }) => {
        const { enable = false, operate = 'GT', args = [] } = value || {};

        const radios = (
          <div className="mt-[4px]">
            <Radio.Group
              disabled={disabled}
              value={enable ? 'limit' : 'all'}
              onChange={({ target: { value } }) =>
                onChange({
                  ...value,
                  enable: value === 'limit',
                  operate: 'GT',
                  args: [0],
                })
              }
              style={{ marginBottom: '8px' }}
            >
              <Radio value="all">{t('aide.不限定')}</Radio>
              <Radio value="limit">{label}</Radio>
            </Radio.Group>
          </div>
        );

        if (!enable) {
          return [radios];
        }

        const select = (
          <Select
            disabled={disabled}
            dropdownMatchSelectWidth={false}
            value={operate}
            onChange={(operate) =>
              onChange({
                ...value,
                operate,
                args: operate === 'BETWEEN' ? [0, 0] : [0],
              })
            }
            onBlur={onBlur}
            options={[
              { value: 'GT', label: t('aide.大于') },
              { value: 'LT', label: t('aide.小于') },
              { value: 'EQ', label: t('aide.等于') },
              { value: 'BETWEEN', label: t('aide.介于') },
            ]}
          />
        );

        const firstNumber = (
          <FormField<
            FV['limitRule']['limitOrderAmount']['args'][0]
          > name="limitRule.limitOrderAmount.args.0">
            {({ field: { value, onChange, onBlur } }) => {
              return (
                <InputNumber
                  disabled={disabled}
                  min={0}
                  max={99999999}
                  precision={0}
                  value={value}
                  onChange={(value) => onChange(value || 0)}
                  onBlur={onBlur}
                  prefix={regionMonetaryUnitMap[toLowerCase(region)]}
                />
              );
            }}
          </FormField>
        );

        if (operate !== 'BETWEEN') {
          return [
            radios,
            <InlineFields>
              {select}
              {firstNumber}
            </InlineFields>,
          ];
        }

        return [
          radios,
          <InlineFields>
            {select}
            {firstNumber}
            <span>～</span>
            <FormField<FV['limitRule']['limitOrderAmount']['args'][1]>
              name="limitRule.limitOrderAmount.args.1"
              validate={(value) => (value <= args[0] ? t('aide.请输入正确的数值') : undefined)}
            >
              {({ field: { value, onChange, onBlur } }) => {
                return (
                  <InputNumber
                    disabled={disabled}
                    min={0}
                    max={99999}
                    precision={0}
                    value={value}
                    onChange={(value) => onChange(value || 0)}
                    onBlur={onBlur}
                    prefix={regionMonetaryUnitMap[toLowerCase(region)]}
                  />
                );
              }}
            </FormField>
          </InlineFields>,
        ];
      }}
    </FormField>
  );
}

export default memo(({ scenarioType, region }: ScenarioTypeProps & RegionProps & DisabledProps) => {
  const enable = !isAeProduct && paymentRelatedScenarios.includes(scenarioType);

  return enable ? <OrderAmountField region={region} /> : <></>;
});

const InlineFields = styled.div`
  display: flex;
  align-items: flex-start;
  > * {
    margin-right: 12px;
  }
  > span {
    line-height: 32px;
  }
  > .ant-select {
    width: auto;
  }
  .ant-input-number-affix-wrapper {
    width: 140px;
  }
`;
